import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const PRODUCT_CARD_IMAGE_SELECTOR = '.product-media__image';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {WeakMap<HTMLImageElement, string>} */
  #defaultImageSizes = new WeakMap();

  #gridObserver = new MutationObserver(() => {
    this.#syncProductCardImageSizes();
    this.#syncInfinitePagination();
  });

  /** @type {{ grid: HTMLUListElement, first: number, last: number, total: number, page: number } | undefined} */
  #paginationBounds;

  /** @type {number | undefined} */
  #paginationFocusFrame;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener('focusout', this.#handlePaginationFocusOut);
    this.#observeGrid();
    this.setAttribute('initialized', '');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeGrid();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('focusout', this.#handlePaginationFocusOut);
    this.#gridObserver.disconnect();
    this.#defaultImageSizes = new WeakMap();
    this.#paginationBounds = undefined;
    if (this.#paginationFocusFrame !== undefined) cancelAnimationFrame(this.#paginationFocusFrame);
    this.#paginationFocusFrame = undefined;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    value = this.#normalizeLayout(value);
    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      try {
        sessionStorage.setItem(`product-grid-view-${viewport}`, value);
      } catch {}
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    value = this.#normalizeLayout(value);
    grid.setAttribute('product-grid-view', value);
    const name = mediaQueryLarge.matches ? 'grid' : 'grid-mobile';
    for (const option of this.querySelectorAll(`input[type="radio"][name="${name}"]`)) {
      if (option instanceof HTMLInputElement) option.checked = option.value === value;
    }
    this.#syncProductCardImageSizes();
  }

  /** @param {string} value */
  #normalizeLayout(value) {
    return !mediaQueryLarge.matches && value === 'mobile-single' ? 'mobile-single' : 'default';
  }

  #observeGrid() {
    this.#gridObserver.disconnect();
    this.#paginationBounds = undefined;

    const { grid } = this.refs;
    if (!grid) return;

    this.#gridObserver.observe(grid, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-last-page'],
    });
    this.#setLayout(grid.getAttribute('product-grid-view') || 'default');
    this.#syncInfinitePagination();
  }

  #syncInfinitePagination() {
    const { grid } = this.refs;
    const wrapper = this.querySelector('[data-qr-infinite-pagination]');
    if (!grid || !wrapper) return;

    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    if (!cards.length) return;

    const first = Number(cards[0]?.getAttribute('data-page'));
    const last = Number(cards[cards.length - 1]?.getAttribute('data-page'));
    const total = Number(grid.dataset.lastPage);
    if (!Number.isInteger(first) || !Number.isInteger(last) || !Number.isInteger(total) || first < 1 || last < first || total < last) return;

    const nav = wrapper.querySelector('.pagination');
    const renderedPage = Number(nav?.getAttribute('data-current_page')) || first;
    const previous = this.#paginationBounds;
    let page = renderedPage;
    if (previous?.grid === grid && previous.total === total && first <= previous.first && last >= previous.last) {
      page = previous.page;
      if (last > previous.last) page = last;
      else if (first < previous.first) page = first;
    }
    this.#paginationBounds = { grid, first, last, total, page };

    if (renderedPage === page || wrapper.contains(this.ownerDocument.activeElement)) return;
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsed = new DOMParser().parseFromString(pageHTML, 'text/html');
    const replacement = parsed.querySelector('[data-qr-infinite-pagination]');
    if (!replacement || Number(replacement.querySelector('.pagination')?.getAttribute('data-current_page')) !== page) return;
    const scope = this.#paginationScope(wrapper);
    if (!scope || scope !== this.#paginationScope(replacement)) return;
    wrapper.replaceChildren(...replacement.childNodes);
  }

  /** @param {Element} wrapper */
  #paginationScope(wrapper) {
    const source = wrapper.getAttribute('data-qr-pagination-scope');
    if (!source) return;
    try {
      const url = new URL(source, this.ownerDocument.baseURI);
      url.searchParams.delete(wrapper.getAttribute('data-qr-pagination-param') || 'page');
      url.searchParams.sort();
      url.hash = '';
      return url.toString();
    } catch {
      return;
    }
  }

  #handlePaginationFocusOut = (event) => {
    if (!event.target?.closest?.('[data-qr-infinite-pagination]')) return;
    if (this.#paginationFocusFrame !== undefined) cancelAnimationFrame(this.#paginationFocusFrame);
    this.#paginationFocusFrame = requestAnimationFrame(() => {
      this.#paginationFocusFrame = undefined;
      if (this.isConnected) this.#syncInfinitePagination();
    });
  };

  #syncProductCardImageSizes = () => {
    const { grid } = this.refs;
    if (!grid) return;

    const useSingleColumnSize = !mediaQueryLarge.matches && grid.getAttribute('product-grid-view') === 'mobile-single';
    const images = /** @type {NodeListOf<HTMLImageElement>} */ (grid.querySelectorAll(PRODUCT_CARD_IMAGE_SELECTOR));

    for (const image of images) {
      if (!this.#defaultImageSizes.has(image)) this.#defaultImageSizes.set(image, image.sizes);

      const sizes = useSingleColumnSize ? '100vw' : this.#defaultImageSizes.get(image);
      if (sizes !== undefined && image.sizes !== sizes) image.sizes = sizes;
    }
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
